import store from "../../store";
import axios from "../common/axios";
import jwtDecode from "jwt-decode";
import { API_URL } from "./constants";
import Operations from "../login/duck/operations";

// Key prefix for chat message drafts in localStorage
const CHAT_DRAFT_PREFIX = "chat_draft_";

// Maximum number of drafts to keep in localStorage
const MAX_DRAFTS = 100;

export const renewAccessToken = () => {
  return new Promise((resolve, reject) => {
    const refreshToken = localStorage.getItem("refreshToken");
    const accessToken = localStorage.getItem("accessToken");
    const { exp } = jwtDecode(accessToken);
    if (refreshToken && Date.now() >= exp * 1000) {
      axios
        .post(`${API_URL}/v1/auth/renew/`, {
          refreshToken: refreshToken,
        })
        .then((res) => {
          if (res.status === 200) {
            store.dispatch(
              Operations.loginUserSuccess(
                res.data.accessToken,
                res.data.refreshToken,
                res.data.profile,
                res.data?.isAccountReactivated || false
              )
            );
            resolve();
          }
        })
        .catch((error) => {
          console.log(error);
          reject();
        });
    } else {
      console.log("not expired", Date.now(), exp * 1000);
      resolve();
    }
  });
};

/**
 * Upload a file to S3 using a pre-signed URL via fetch.
 * Retries up to 2 times on network failures (not HTTP errors).
 * Includes 2-minute timeout per attempt.
 */
export const uploadFileToS3 = async (file, signedUrl, { isPublic = false, scrubFilename, signal } = {}) => {
  const maxRetries = 2;
  const timeoutMs = 120000;
  let lastError = null;

  // Note: signedUrl is reused across retries. S3 signed URLs expire in 15 minutes
  // (configured in s3.service.ts). With 2-min timeout + 2 retries, worst case is ~6 min.
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      const onAbort = signal ? () => controller.abort() : null;

      if (signal) {
        if (signal.aborted) {
          clearTimeout(timeoutId);
          throw new DOMException("Upload aborted", "AbortError");
        }
        signal.addEventListener("abort", onAbort, { once: true });
      }

      const disposition = file.type?.startsWith("image/") ? "inline" : "attachment";
      const sanitized = (scrubFilename && scrubFilename(file.name)) || file.name;

      let response;
      try {
        response = await fetch(signedUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
            "x-amz-acl": isPublic ? "public-read" : "private",
            "Content-Disposition": `${disposition}; filename="${sanitized}"`,
          },
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeoutId);
        if (onAbort) signal.removeEventListener("abort", onAbort);
      }

      if (!response.ok) {
        const err = new Error(`S3 returned ${response.status}: ${response.statusText}`);
        err.isHttpError = true;
        throw err;
      }

      return;
    } catch (err) {
      lastError = err;
      if (err.isHttpError) throw err;
      if (err.name === "AbortError") throw err;
      if (attempt < maxRetries) {
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
  }

  throw lastError;
};

export const getS3SignedUrl = (
  file,
  callback,
  {
    scrubFilename = () => false,
    path = "/",
    isPublic = false,
    onSignedUrl = () => {},
    onError = () => {},
  }
) => {
  const fileName = scrubFilename(file.name);
  const params = {
    objectName: fileName || file.name,
    contentType: file.type,
    path: path, //string equivalent to s3path ex. - `public/profile/${userID}/avatar/`
  };
  if (isPublic) {
    params.isPublic = true;
  }
  axios
    .get(`${API_URL}/v1/asset/s3/sign`, { params })
    .then(({ data }) => {
      onSignedUrl();
      callback(data);
    })
    .catch((error) => {
      console.error(error);
      onError(error);
    });
};

/**
 * Gets all chat drafts from localStorage with their timestamps
 * @returns {Array} Array of {key, timestamp, text} objects
 */
const getAllChatDrafts = () => {
  const drafts = [];

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      // Check if this is a chat draft key
      if (key && key.startsWith(CHAT_DRAFT_PREFIX)) {
        const draftDataString = localStorage.getItem(key);

        if (!draftDataString) continue;

        try {
          const draftData = JSON.parse(draftDataString);

          // New format with timestamp
          if (draftData && draftData.timestamp && draftData.text) {
            drafts.push({
              key: key,
              timestamp: new Date(draftData.timestamp).getTime(),
              text: draftData.text,
            });
          } else {
            // Old format without timestamp - assign timestamp 0 so it gets deleted first
            drafts.push({
              key: key,
              timestamp: 0,
              text: draftDataString,
            });
          }
        } catch {
          // Plain text format (old/legacy) - assign timestamp 0 so it gets deleted first
          drafts.push({
            key: key,
            timestamp: 0,
            text: draftDataString,
          });
        }
      }
    }
  } catch (error) {
    console.error("Error getting all chat drafts:", error);
  }

  return drafts;
};

/**
 * Gets count of chat drafts without parsing them (lightweight)
 */
const getChatDraftsCount = () => {
  let count = 0;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CHAT_DRAFT_PREFIX)) {
        count++;
      }
    }
  } catch (error) {
    console.error("Error counting chat drafts:", error);
  }
  return count;
};

/**
 * Cleans up oldest drafts if total count exceeds MAX_DRAFTS
 */
const cleanupOldestDrafts = () => {
  try {
    // Fast check: only do expensive operations if we might need to clean up
    const draftCount = getChatDraftsCount();

    // Only cleanup if we exceed the limit
    if (draftCount > MAX_DRAFTS) {
      const allDrafts = getAllChatDrafts();

      // Sort by timestamp (oldest first)
      allDrafts.sort((a, b) => a.timestamp - b.timestamp);

      // Calculate how many to delete
      const draftsToDelete = allDrafts.length - MAX_DRAFTS;

      // Delete the oldest ones
      for (let i = 0; i < draftsToDelete; i++) {
        localStorage.removeItem(allDrafts[i].key);
      }

      console.info(
        `Cleaned up ${draftsToDelete} oldest chat drafts (limit: ${MAX_DRAFTS})`
      );
    }
  } catch (error) {
    console.error("Error cleaning up oldest drafts:", error);
  }
};

/**
 * Saves a message draft for a specific conversation
 * @param {string} conversationId - The ID of the conversation
 * @param {string} text - The draft message text
 * @param {string} topicId - Optional topic ID for multi-topic conversations
 */
export const saveChatDraft = (conversationId, text, topicId = null) => {
  try {
    if (!conversationId) return;

    // Build storage key: include topicId if present, otherwise just conversationId
    const storageKey = topicId
      ? `${CHAT_DRAFT_PREFIX}${conversationId}_${topicId}`
      : `${CHAT_DRAFT_PREFIX}${conversationId}`;

    // Only save if there's actual text content
    if (text && text.trim().length > 0) {
      const draftData = {
        text: text,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem(storageKey, JSON.stringify(draftData));

      // Clean up oldest drafts if we exceed the limit
      cleanupOldestDrafts();
    } else {
      // If the text is empty, remove any existing draft
      localStorage.removeItem(storageKey);
    }
  } catch (error) {
    console.error("Error saving chat draft:", error);
  }
};

/**
 * Retrieves a message draft for a specific conversation
 * @param {string} conversationId - The ID of the conversation
 * @param {string} topicId - Optional topic ID for multi-topic conversations
 * @returns {string} The draft message text or empty string if no draft exists
 */
export const getChatDraft = (conversationId, topicId = null) => {
  try {
    if (!conversationId) return "";

    // Build storage key: include topicId if present, otherwise just conversationId
    const storageKey = topicId
      ? `${CHAT_DRAFT_PREFIX}${conversationId}_${topicId}`
      : `${CHAT_DRAFT_PREFIX}${conversationId}`;

    const draftDataString = localStorage.getItem(storageKey);

    if (!draftDataString) return "";

    // Try to parse as JSON (new format with timestamp)
    try {
      const draftData = JSON.parse(draftDataString);

      // Check if it's the new format with timestamp and text
      if (draftData && draftData?.timestamp && draftData?.text) {
        return draftData.text;
      }
    } catch {
      console.warn("Draft data is not in JSON format, treating as plain text.");
    }

    return "";
  } catch (error) {
    console.error("Error retrieving chat draft:", error);
    return "";
  }
};

/**
 * Clears a message draft for a specific conversation
 * @param {string} conversationId - The ID of the conversation
 * @param {string} topicId - Optional topic ID for multi-topic conversations
 */
export const clearChatDraft = (conversationId, topicId = null) => {
  try {
    if (!conversationId) return;

    // Build storage key: include topicId if present, otherwise just conversationId
    const storageKey = topicId
      ? `${CHAT_DRAFT_PREFIX}${conversationId}_${topicId}`
      : `${CHAT_DRAFT_PREFIX}${conversationId}`;

    localStorage.removeItem(storageKey);
  } catch (error) {
    console.error("Error clearing chat draft:", error);
  }
};
